import { alpha } from "@mui/material/styles";
import {
    Box,
    Button,
    Checkbox,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Stack,
    Tooltip,
    Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import FolderSharedOutlinedIcon from '@mui/icons-material/FolderSharedOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { useAuth } from "../../store/auth-context";
import ResponsiveDialog from "../_atoms/ResponsiveDialog";

const ORIENTATION_ANALYSIS_KEY = 'case_formulation';

const getSelectableItemSx = (theme) => ({
    borderRadius: 1.5,
    padding: 0,
    transition: theme.transitions.create('background-color', {
        duration: theme.transitions.duration.shortest
    }),
    '&:hover': {
        backgroundColor: alpha(theme.palette.info.light, 0.18)
    },
    '&.Mui-selected': {
        backgroundColor: alpha(theme.palette.info.main, 0.12)
    },
    '&.Mui-selected:hover': {
        backgroundColor: alpha(theme.palette.info.main, 0.18)
    }
});

const getSupportSectionSx = (theme, isActive) => ({
    borderRadius: 2,
    border: `1px solid ${isActive ? alpha(theme.palette.info.main, 0.42) : alpha(theme.palette.grey[500], 0.22)}`,
    backgroundColor: isActive ? alpha(theme.palette.info.main, 0.06) : theme.palette.common.white,
    px: 1.5,
    py: 1.35
});

const FunctionCheckboxList = ({ items, selectedKeys, onToggle, disabled }) => (
    <List disablePadding dense sx={{ pt: 0.5, maxHeight: 196, overflowY: 'auto' }}>
        {items.map((item) => (
            <ListItem key={item.key} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                    selected={selectedKeys.includes(item.key)}
                    onClick={() => onToggle(item.key)}
                    disabled={disabled}
                    dense
                    sx={getSelectableItemSx}
                >
                    <Checkbox
                        checked={selectedKeys.includes(item.key)}
                        tabIndex={-1}
                        disableRipple
                        onClick={(event) => event.stopPropagation()}
                        onChange={() => onToggle(item.key)}
                        sx={{ mr: 1, mt: 0.25 }}
                    />
                    <ListItemText
                        primary={item.title}
                        primaryTypographyProps={{
                            sx: {
                                whiteSpace: 'normal',
                                overflowWrap: 'anywhere'
                            }
                        }}
                        sx={{ minWidth: 0 }}
                    />
                </ListItemButton>
            </ListItem>
        ))}
    </List>
);

const PatientAssistantSidebar = ({
    patientName,
    files = [],
    selectedFileIds = [],
    selectedAnalysis = [],
    selectedExtra = [],
    analysisCatalog = [],
    extraCatalog = [],
    onStart,
    loading = false,
    isJobActive
}) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    const [isConfiguratorOpen, setIsConfiguratorOpen] = useState(false);
    const [selectedTranscriptionIds, setSelectedTranscriptionIds] = useState(selectedFileIds);
    const [draftAnalysis, setDraftAnalysis] = useState([ORIENTATION_ANALYSIS_KEY]);
    const isVerifiedAdmin = user?.role === 'admin' && user?.adminSessionActive === true;
    const patientSupportLabel = patientName
        ? `Configura il supporto AI per il paziente ${patientName}`
        : 'Configura il supporto AI per il paziente';
    const canSubmitAnalysis = selectedTranscriptionIds.length > 0;

    useEffect(() => {
        if (selectedFileIds.length > 0 && selectedTranscriptionIds[0] !== selectedFileIds[0]) {
            setSelectedTranscriptionIds([selectedFileIds[0]]);
            return;
        }

        if (selectedTranscriptionIds.length === 0 && files.length > 0) {
            setSelectedTranscriptionIds([files[0].id]);
        }
    }, [files, selectedFileIds, selectedTranscriptionIds]);

    useEffect(() => {
        setDraftAnalysis([ORIENTATION_ANALYSIS_KEY]);
    }, [selectedAnalysis]);

    const toggleTranscriptionSelection = (fileId) => {
        setSelectedTranscriptionIds([fileId]);
    };

    const previewAnalyzeHandler = async () => {
        if (!canSubmitAnalysis || typeof onStart !== 'function') {
            return;
        }

        const hasStarted = await onStart({
            fileIds: selectedTranscriptionIds,
            analysisFunctions: [ORIENTATION_ANALYSIS_KEY],
            extraFunctions: []
        });
        if (hasStarted) {
            setIsConfiguratorOpen(false);
        }
    };

    const assistantButtonSx = {
        color: '#2196f3',
        backgroundColor: 'transparent',
        border: '1px solid #2196f3',
        boxShadow: 'none',
        cursor: 'pointer',
        fontSize: { xs: '0.95rem', sm: '1rem' },
        fontWeight: 700,
        minWidth: 'fit-content',
        alignSelf: 'stretch',
        px: 1.4,
        py: 0.75,
        justifyContent: 'center',
        borderRadius: 1.5,
        '& .MuiButton-startIcon': {
            mr: 0.75
        },
        '& .MuiButton-startIcon > *:first-of-type': {
            fontSize: '1.35em'
        },
        '&:hover': {
            backgroundColor: alpha('#2196f3', 0.05),
            boxShadow: 'none',
            borderColor: '#2196f3'
        }
    };

    return (
        <>
            <Stack spacing={2}>
                <Stack
                    spacing={1}
                    sx={{
                        backgroundColor: (theme) => theme.palette.grey[100],
                        borderRadius: 1.5,
                        px: 1.5,
                        py: 1.25
                    }}
                >
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Tooltip title="info Assistente" placement="top">
                            <IconButton
                                size="small"
                                aria-label="Apri informazioni assistente AI"
                                onClick={() => setIsHelpOpen(true)}
                                sx={{
                                    border: (theme) => `1px solid ${theme.palette.primary.main}`,
                                    color: 'primary.main',
                                    width: 20,
                                    height: 20,
                                    p: 0
                                }}
                            >
                                <Typography variant="caption" fontWeight="bold" color="inherit">?</Typography>
                            </IconButton>
                        </Tooltip>
                    </Stack>
                    {isVerifiedAdmin && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<TuneIcon />}
                            onClick={() => navigate('/admin-dashboard')}
                            sx={{ alignSelf: 'flex-start', mt: 0.25 }}
                        >
                            Admin Dashboard
                        </Button>
                    )}
                </Stack>

                <Button
                    variant="text"
                    size="large"
                    startIcon={<AutoAwesomeOutlinedIcon />}
                    onClick={() => setIsConfiguratorOpen(true)}
                    sx={assistantButtonSx}
                >
                    Analizza con l&apos;AI
                </Button>
            </Stack>

            <ResponsiveDialog
                isOpen={isConfiguratorOpen}
                onClose={() => setIsConfiguratorOpen(false)}
                title={(
                    <Stack spacing={0.35} alignItems="center" textAlign="center">
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            <AutoAwesomeIcon sx={{ color: '#2196f3', fontSize: '1.1rem' }} />
                            <Typography variant="h6" fontWeight="bold">Analizza AI</Typography>
                            <AutoAwesomeIcon sx={{ color: '#2196f3', fontSize: '1.1rem' }} />
                        </Stack>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                            {patientSupportLabel}
                        </Typography>
                    </Stack>
                )}
                showCloseButton
                minWidth="640px"
                minHeight="unset"
                paperSx={{
                    borderRadius: 2,
                    width: '100%',
                    maxWidth: { xs: 'calc(100vw - 24px)', sm: '640px' },
                    overflowX: 'hidden'
                }}
                titleSx={{ py: 1.75, px: { xs: 2, sm: 3 }, borderBottom: 'none' }}
                contentSx={{ px: { xs: 2, sm: 3 }, py: 1, width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}
                actionsSx={{ px: 3, py: 1.5, justifyContent: 'space-between', gap: 1.5 }}
                actions={(
                    <>
                        <Button variant="outlined" onClick={() => setIsConfiguratorOpen(false)}>
                            Annulla
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<AutoAwesomeOutlinedIcon />}
                            onClick={previewAnalyzeHandler}
                            disabled={!canSubmitAnalysis || loading}
                        >
                            Analizza con l&apos;AI
                        </Button>
                    </>
                )}
            >
                <Stack spacing={2.25} width="100%" maxWidth="100%" sx={{ overflowX: 'hidden' }}>
                    <Box
                        sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            px: { xs: 1.25, sm: 1.5 },
                            py: 1.5,
                            backgroundColor: '#f3f4f6'
                        }}
                    >
                        <Stack spacing={1.25}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                Seleziona la trascrizione
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Per questo test e attiva solo l&apos;analisi di una singola trascrizione per orientamento.
                            </Typography>
                            <List disablePadding dense sx={{ maxHeight: 220, overflow: 'auto' }}>
                                {files.map((file) => (
                                    <ListItem key={file.id} disablePadding sx={{ mb: 0.75 }}>
                                        <ListItemButton
                                            selected={selectedTranscriptionIds.includes(file.id)}
                                            onClick={() => toggleTranscriptionSelection(file.id)}
                                            dense
                                            sx={getSelectableItemSx}
                                        >
                                            <Checkbox
                                                checked={selectedTranscriptionIds.includes(file.id)}
                                                tabIndex={-1}
                                                disableRipple
                                                onClick={(event) => event.stopPropagation()}
                                            onChange={() => toggleTranscriptionSelection(file.id)}
                                                sx={{ mr: 1, mt: 0.25 }}
                                            />
                                            <ListItemText
                                                primary={file.name}
                                                primaryTypographyProps={{
                                                sx: {
                                                    whiteSpace: 'normal',
                                                    overflowWrap: 'anywhere',
                                                    fontWeight: 400
                                                }
                                            }}
                                        />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                                {files.length === 0 && (
                                    <Typography variant="body2" sx={{ opacity: 0.72 }}>
                                        Nessun file disponibile per questo paziente.
                                    </Typography>
                                )}
                            </List>
                        </Stack>
                    </Box>

                    <Box
                        sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            px: { xs: 1.25, sm: 1.5 },
                            py: 1.5,
                            backgroundColor: '#f8fafc'
                        }}
                    >
                        <Stack spacing={1.5}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                Tipo di supporto AI:
                            </Typography>

                            <Stack spacing={1.25}>
                                <Stack sx={(theme) => getSupportSectionSx(theme, true)}>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        Analisi seduta
                                    </Typography>
                                    <FunctionCheckboxList
                                        items={analysisCatalog}
                                        selectedKeys={draftAnalysis}
                                        onToggle={() => {}}
                                        disabled
                                    />
                                </Stack>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="flex-start"
                                    sx={{
                                        backgroundColor: '#fff4cc',
                                        border: '1px solid #f2df9b',
                                        borderRadius: 1.5,
                                        px: 1.25,
                                        py: 1
                                    }}
                                >
                                    <WarningAmberRoundedIcon sx={{ color: '#8a5a12', fontSize: '1.1rem', mt: '2px' }} />
                                    <Typography variant="body2" sx={{ color: '#8a5a12' }}>
                                        Per questo test sono temporaneamente disattivati monitoraggio percorso, analisi extra e altri parametri AI. Rimane attiva solo l&apos;analisi per orientamento.
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Stack>
                    </Box>

                </Stack>
            </ResponsiveDialog>

            <ResponsiveDialog
                isOpen={isHelpOpen}
                onClose={() => setIsHelpOpen(false)}
                title="Istruzione Assistente Ai"
                showCloseButton
                minWidth="560px"
                minHeight="unset"
            >
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <Typography variant="body1">
                        La modal di configurazione ti permette di scegliere una trascrizione della cartella paziente
                        e di avviare temporaneamente solo l&apos;analisi seduta per orientamento.
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.78 }}>
                        Tutte le altre opzioni del flusso AI sono state disattivate temporaneamente per verificare in modo isolato il passaggio dell&apos;orientamento primario.
                    </Typography>
                </Stack>
            </ResponsiveDialog>
        </>
    );
};

export default PatientAssistantSidebar;
